import { useMemo, useState } from "react"
import { useQuery } from "react-query"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/outline"
import { PageView, PageTopBar } from "../../components"
import { Loader } from "../../components/Loader"
import { fetchShiftSchedules, fetchWorkShifts } from "../../services/workforceServices"
import { WorkforceTabs } from "./WorkforceTabs"
import { ScheduleTimeline } from "./components/ScheduleTimeline"
import { SchedulePeriodFormDialog } from "./components/SchedulePeriodFormDialog"
import { PeriodRosterSlidePanel } from "./components/PeriodRosterSlidePanel"

const pad = (n) => String(n).padStart(2, '0')
const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

const firstOfMonth = (anchor) => new Date(anchor.getFullYear(), anchor.getMonth(), 1)
const lastOfMonth = (anchor) => new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0)

export const ShiftScheduleContainer = () => {
    const { warehouse_id } = useParams()
    const { t } = useTranslation()

    const [anchor, setAnchor] = useState(() => firstOfMonth(new Date()))
    const [shiftFilter, setShiftFilter] = useState('')
    const [formState, setFormState] = useState(null) // null | { period?, defaults? }
    const [rosterPeriod, setRosterPeriod] = useState(null)

    const from = useMemo(() => toIso(firstOfMonth(anchor)), [anchor])
    const to = useMemo(() => toIso(lastOfMonth(anchor)), [anchor])

    const { data: shiftsData } = useQuery(
        ['workforce', warehouse_id, 'shifts', { include_inactive: true }],
        () => fetchWorkShifts(warehouse_id, { include_inactive: true }),
        { refetchOnWindowFocus: false }
    )
    const shifts = useMemo(() => {
        const raw = Array.isArray(shiftsData) ? shiftsData : (shiftsData?.work_shifts || shiftsData?.shifts || [])
        return raw
    }, [shiftsData])

    const schedulesQueryKey = ['workforce', warehouse_id, 'schedules', { from, to, work_shift_id: shiftFilter || null }]
    const { data: schedulesData, isLoading } = useQuery(
        schedulesQueryKey,
        () => fetchShiftSchedules(warehouse_id, {
            from, to,
            ...(shiftFilter ? { work_shift_id: shiftFilter } : {}),
        }),
        { keepPreviousData: true }
    )
    const schedules = useMemo(() => {
        const raw = Array.isArray(schedulesData) ? schedulesData : (schedulesData?.shift_schedules || schedulesData?.schedules || [])
        return raw
    }, [schedulesData])

    const monthLabel = useMemo(() => {
        return anchor.toLocaleDateString(undefined, { year: 'numeric', month: 'long' })
    }, [anchor])

    const goPrev = () => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1))
    const goNext = () => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1))
    const goToday = () => setAnchor(firstOfMonth(new Date()))

    return (
        <>
            <PageView
                topMenu={
                    <PageTopBar>
                        <div className="text-lg font-semibold">{t('workforce.schedule.title')}</div>
                        <button
                            type="button"
                            onClick={() => setFormState({ defaults: { work_shift_id: shiftFilter || '', start_date: from, end_date: from } })}
                            className="inline-flex items-center rounded-lg bg-yellow-500 px-3 py-2 text-sm font-medium text-white hover:bg-yellow-600"
                        >
                            + {t('workforce.schedule.schedule_shift')}
                        </button>
                    </PageTopBar>
                }
                topMenuFullWidth={true}
                childrenFullWidth={true}
            >
                <WorkforceTabs />

                <div className="mt-4 flex flex-wrap items-center gap-3">
                    <div className="inline-flex items-center rounded-md border border-gray-300 overflow-hidden">
                        <button type="button" onClick={goPrev} className="px-2 py-1.5 bg-white hover:bg-gray-50 text-gray-600"><ChevronLeftIcon className="h-4 w-4" /></button>
                        <button type="button" onClick={goToday} className="px-3 py-1.5 bg-white hover:bg-gray-50 text-sm text-gray-700 border-x border-gray-200">{t('workforce.schedule.today')}</button>
                        <button type="button" onClick={goNext} className="px-2 py-1.5 bg-white hover:bg-gray-50 text-gray-600"><ChevronRightIcon className="h-4 w-4" /></button>
                    </div>
                    <div className="text-base font-medium text-gray-800 capitalize">{monthLabel}</div>
                    <select
                        value={shiftFilter}
                        onChange={(e) => setShiftFilter(e.target.value)}
                        className="ml-auto border border-gray-300 rounded-md text-sm px-3 py-1.5 bg-white"
                    >
                        <option value="">{t('workforce.schedule.all_shifts')}</option>
                        {shifts.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </div>

                {isLoading ? (
                    <div className="mt-6"><Loader show={true} /></div>
                ) : (
                    <div className="mt-4">
                        <ScheduleTimeline
                            anchor={anchor}
                            schedules={schedules}
                            shifts={shifts}
                            shiftFilter={shiftFilter}
                            onPeriodClick={(period) => setRosterPeriod(period)}
                            onEmptyDayClick={(dateIso, workShiftId) => setFormState({ defaults: { work_shift_id: workShiftId, start_date: dateIso, end_date: dateIso } })}
                        />
                    </div>
                )}
            </PageView>

            {formState && (
                <SchedulePeriodFormDialog
                    warehouse_id={warehouse_id}
                    shifts={shifts}
                    period={formState.period || null}
                    defaults={formState.defaults || {}}
                    onClose={() => setFormState(null)}
                />
            )}

            {rosterPeriod && (
                <PeriodRosterSlidePanel
                    warehouse_id={warehouse_id}
                    period={rosterPeriod}
                    onEdit={() => { const p = rosterPeriod; setRosterPeriod(null); setFormState({ period: p }) }}
                    onClose={() => setRosterPeriod(null)}
                />
            )}
        </>
    )
}
